import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiConsumer } from '../../service/api-consumer';
import { GeoFiltersRequest } from '../../interface/request/GeoFiltersRequest';
import { isGeoColumn } from '../../util/table-helper';
import { GeoClass, GeoFiltersData } from '../../interface/response/GeoFiltersResponse';

/**
 * `filtersSlice` manages hierarchical geo-filter state in the application
 * (e.g., continent → country → region → city).
 *
 * ---
 * ## `fetchGeoFilters.fulfilled` reducer
 * Triggered when geo filter data is successfully loaded from the API.
 *
 * ### General behavior:
 * - Saves the fetched data into `geoFiltersData`.
 * - Ensures the following structures exist:
 *   - `allOptionsFull`   → full, accumulated list of all options ever fetched
 *   - `allOptionsFiltered` → currently valid options based on user selection
 * - Reads the following metadata from the thunk arguments:
 *   - `currentColumn`  → the column for which data was just fetched
 *   - `currentFilters` → filter state after user interaction
 *
 * ### Column indexing
 * Columns are processed as an ordered hierarchy:
 * `columns = Object.keys(newData).filter(isGeoColumn)`
 *
 * `currentColumn` determines the index `columnIndex`, which controls how
 * each level is updated.
 *
 * ---
 * ## Updating `allOptionsFull`
 * For every column:
 * - Keeps a full cached list of *all options ever fetched* for that column.
 *
 * ---
 * ## Updating `allOptionsFiltered`
 * The behavior depends on the column’s position relative to `currentColumn`.
 *
 * ### 1. Levels above `currentColumn` (i < columnIndex)
 * These levels represent already-selected parent levels.
 * - They remain unchanged.
 * - If they do not yet exist, they are initialized with the incoming options.
 *
 * ### 2. The current level (i === columnIndex)
 * This is the level the user is interacting with.
 * - The reducer merges new incoming options with previously filtered ones.
 *
 * ### 3. The next level (i === columnIndex + 1)
 * This level depends directly on the user's selection at `currentColumn`.
 * - It is completely replaced with `incoming`.
 * - Meaning: it shows only the children of the newly selected items.
 *
 * ---
 * ## `resetGeoFilters` reducer
 * Resets active geo filter selection while keeping cached data.
 *
 * ### Behavior:
 * - Clears `currentFilters`
 * - Clears `allOptionsFiltered`
 * - Resets `geoFiltersData` to an empty/null structure
 * - **Does NOT clear `allOptionsFull`**
 *
 * ### Purpose:
 * - To reset the UI state (e.g., "Clear filters") without losing already-fetched data.
 * - Ensures dependent columns start fresh without triggering unnecessary API calls.
 */

export const fetchGeoFilters = createAsyncThunk('filters/fetchGeoFilters', async (payload: GeoFiltersRequest) => {
  const response = await ApiConsumer.getGeoFilters(payload);
  return response;
});

interface GeoFiltersState {
  geoFiltersData: GeoFiltersData;
  currentFilters: Record<string, string | number | boolean>;
  allOptionsFiltered: Record<string, any>;
  allOptionsFull: Record<string, GeoClass[]>;
  loading: boolean;
  error: null | unknown;
  openFilterDropDown: boolean;
  lastFetchedStartDate: string | null | undefined;
}

const initialState: GeoFiltersState = {
  geoFiltersData: {
    Client: null,
    Company: null,
    Region: null,
    Group: null,
    Location: null,
  },
  allOptionsFiltered: {},
  currentFilters: {},
  allOptionsFull: {},
  loading: false,
  error: null,
  openFilterDropDown: false,
  lastFetchedStartDate: undefined,
};

const filtersSlice = createSlice({
  name: 'geoFilters',
  initialState,
  reducers: {
    resetGeoFilters: (state) => {
      state.allOptionsFiltered = {};
      state.currentFilters = {};
      state.geoFiltersData = {
        Client: null,
        Company: null,
        Region: null,
        Group: null,
        Location: null,
      };
      state.openFilterDropDown = false;
    },
    toggleFilterDropdown: (state) => {
      state.openFilterDropDown = !state.openFilterDropDown;
    },
    closeFilterDropdown: (state) => {
      state.openFilterDropDown = false;
    },
    openFilterDropdown: (state) => {
      state.openFilterDropDown = true;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchGeoFilters.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchGeoFilters.fulfilled, (state, action) => {
        const newData = action.payload;
        const { currentColumn = '', currentFilters = {} } = action.meta.arg;
        const { StartDate } = action.meta.arg;
        state.lastFetchedStartDate = StartDate;
        state.geoFiltersData = newData;
        state.currentFilters = currentFilters;

        if (!state.allOptionsFiltered) state.allOptionsFiltered = {};
        if (!state.allOptionsFull) state.allOptionsFull = {};

        const columns = Object.keys(newData).filter(isGeoColumn);
        const columnIndex = columns.indexOf(currentColumn);

        // -----------------------------
        // Helpers
        // -----------------------------

        const mergeUnique = (oldArr: GeoClass[], incoming: GeoClass[]) => {
          const ids = new Set(oldArr.map((x) => x.ID));
          return [...oldArr, ...incoming.filter((x) => !ids.has(x.ID))];
        };

        const setIfEmpty = (col: string, incoming: GeoClass[]) => {
          if (!state.allOptionsFiltered[col]) {
            state.allOptionsFiltered[col] = incoming;
          }
        };

        // -----------------------------
        // UPDATE FULL OPTIONS
        // -----------------------------

        for (const col of columns) {
          const incoming = newData[col] || [];
          const existingFull = state.allOptionsFull[col] || [];
          state.allOptionsFull[col] = mergeUnique(existingFull, incoming);
        }

        // -----------------------------
        // DATE REFRESH branch (no currentColumn provided)
        // Triggered when only the date range changed. We replace each
        // column's options with what the API returns for the new date,
        // so options that are no longer valid for this date disappear.
        // If the API didn't return a column (null, e.g. because no
        // parent ID was sent), we leave its existing options untouched.
        // The current selection (currentFilters) is intentionally left
        // alone here - stale selections are cleaned up separately in the
        // UI once the options no longer contain them.
        // -----------------------------

        if (columnIndex === -1) {
          columns.forEach((col) => {
            const incoming = newData[col];
            if (incoming) {
              state.allOptionsFiltered[col] = incoming;
            }
          });
          return;
        }

        // -----------------------------
        // Normal cascade logic (user interacted with a specific column)
        // -----

        state.currentFilters = currentFilters;

        // -----------------------------
        // UPDATE FILTERED OPTIONS
        // -----------------------------

        columns.forEach((col, i) => {
          const incoming = newData[col] || [];

          if (i < columnIndex) {
            // Parent levels
            setIfEmpty(col, incoming);
            return;
          }

          if (i === columnIndex) {
            // Current level
            const existing = state.allOptionsFiltered[col] || [];
            state.allOptionsFiltered[col] = mergeUnique(existing, incoming);
            return;
          }

          if (i === columnIndex + 1) {
            // Direct child level
            state.allOptionsFiltered[col] = incoming;
            return;
          }

          // Deeper levels
          state.allOptionsFiltered[col] = [];
        });
      })
      .addCase(fetchGeoFilters.rejected, (state, action) => {
        state.error = action.error;
      });
  },
});
export const { resetGeoFilters, toggleFilterDropdown, openFilterDropdown, closeFilterDropdown } = filtersSlice.actions;
export default filtersSlice.reducer;
